/**
 * Notifications dropdown: grouped list, mark-all-read, and infinite scroll.
 */
import * as React from 'react';
import { CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/forms/Button';
import { ScrollBar } from '@/components/ui/scroll-bar';
import type { AppNotification } from '@/types/notification.types';
import { NotificationBadgeCount } from './NotificationBadgeCount';
import { NotificationItem } from './NotificationItem';
import { NotificationsEmptyState } from './NotificationsEmptyState';
import { NotificationsSkeleton } from './NotificationsSkeleton';
import {
  NOTIFICATION_BUCKET_LABELS,
  NOTIFICATION_SCROLL_LOAD_THRESHOLD_PX,
  type NotificationBucket,
} from './notification-configs';
import { groupByBucket } from './notification-utils';

/** Props for {@link NotificationsPanel}. */
interface NotificationsPanelProps {
  items: AppNotification[];
  /** API total across all pages. */
  total?: number;
  unreadCount: number;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isMarkingAllRead?: boolean;
  onItemClick?: (notification: AppNotification) => void;
  onMarkAllRead?: () => void;
  className?: string;
}

/** Notifications dropdown: infinite list, mark-read, and deep links. */
export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  items,
  total,
  unreadCount,
  isLoading = false,
  isFetchingMore = false,
  hasMore = false,
  onLoadMore,
  isMarkingAllRead = false,
  onItemClick,
  onMarkAllRead,
  className,
}) => {
  const groups = React.useMemo(() => groupByBucket(items), [items]);
  const hasItems = items.length > 0;
  const totalCount = total ?? items.length;

  let footerCount = `${items.length} sur ${totalCount}`;
  if (items.length >= totalCount) {
    footerCount = `${totalCount} notification${totalCount > 1 ? 's' : ''}`;
  }

  function renderNotificationRow(notification: AppNotification): React.ReactNode {
    return (
      <NotificationItem key={notification.id} notification={notification} onClick={onItemClick} />
    );
  }

  function renderBucketGroup(group: [NotificationBucket, AppNotification[]]): React.ReactNode {
    const [bucket, notifications] = group;
    return (
      <section key={bucket} className="notifications-dropdown-group">
        <div className="notifications-dropdown-group-label">
          {NOTIFICATION_BUCKET_LABELS[bucket]}
        </div>
        {notifications.map(renderNotificationRow)}
      </section>
    );
  }

  function renderLoadingMore(): React.ReactNode {
    if (!isFetchingMore) return null;
    return (
      <div className="notifications-dropdown-loading-more" aria-live="polite">
        Chargement…
      </div>
    );
  }

  function renderPanelBody(): React.ReactNode {
    if (isLoading) return <NotificationsSkeleton />;
    if (!hasItems) return <NotificationsEmptyState />;
    return (
      <>
        {groups.map(renderBucketGroup)}
        {renderLoadingMore()}
      </>
    );
  }

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || isFetchingMore || !onLoadMore) return;
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < NOTIFICATION_SCROLL_LOAD_THRESHOLD_PX) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, onLoadMore],
  );

  return (
    <div className={cn('notifications-dropdown', className)}>
      <header className="notifications-dropdown-header">
        <div className="notifications-dropdown-header-left">
          <span className="notifications-dropdown-title">Notifications</span>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="h-5 gap-1 px-2 text-[10px] tracking-wide">
              <NotificationBadgeCount count={unreadCount} />
              <span>non lue{unreadCount > 1 ? 's' : ''}</span>
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={CheckCheck}
          iconPosition="left"
          onClick={onMarkAllRead}
          disabled={unreadCount === 0 || isMarkingAllRead}
          loading={isMarkingAllRead}
        >
          Tout marquer comme lu
        </Button>
      </header>

      <ScrollBar maxHeight="460px" className="notifications-dropdown-body" onScroll={handleScroll}>
        {renderPanelBody()}
      </ScrollBar>

      {hasItems && (
        <footer className="notifications-dropdown-footer">
          <span className="notifications-dropdown-footer-count">{footerCount}</span>
        </footer>
      )}
    </div>
  );
};
